import type { IBaseComponentProps } from 'components/base/types'
import type { HydratedFormLayoutProps, IForm, ReactiveFormLayoutProps } from './form'
import type { TFormModel } from './formModel'
import type { IFormFieldAttributes, TFormFactor } from './attributes'
import type { Ref } from 'vue'
import type { FormV2Stats } from '../formv2/types'
import type { FormV2UserState } from './state'
import type { SelectComponentMap } from '../form-field/types'
import type { UseFormReturn } from '../formv2/hooks/useForm'
import type { StringIndexable } from '@/types/object'
import type { FormFieldRef, TFormFieldDict } from './field'

export type FormSchemaReducer<T extends TFormModel = { [key: string]: any }> = (
  schema: IForm<T>,
  state: T
) => IForm<T>

export type IFormProps<T extends TFormModel = { [key: string]: any }> = IBaseComponentProps &
  IFormFieldAttributes<T> & {
    schema: IForm<T, T>
    layout?: ReactiveFormLayoutProps<T>[];
    initialState?: FormV2UserState<T>;
    syncedState?: Ref<FormV2UserState<T> | null | undefined>;
    onInput?: (event: Event, meta?: any) => void
    onStateBlur?: (event: FocusEvent, meta?: any) => void
    onStateChange?: (state: T) => void | Promise<void>;
    onSubStateChange?: (state: T, key?: string) => void
    schemaReducers?: FormSchemaReducer<T>[]
    topState?: T
    name?: string
    keys?: string[]
    depth?: number // used to check how deep the recursion is
    outputState?: Ref<T>
    outputStates?: Ref<T>[]
    outputStats?: Ref<FormV2Stats>
    outputStatsList?: Ref<FormV2Stats>[]
    outputErrorCount?: Ref<number>
    componentExtensions?: SelectComponentMap
    validateImmediately?: boolean;
    hideErrorsUntilTouched?: boolean;
    debug?: boolean
  }

export type GeneralFormProps<T extends TFormModel = TFormModel> = Partial<IFormProps<T>> & {
  handler?: UseFormReturn<T>
}

export const FORM_PROPS = [
  'schema',
  'initialState',
  'onStateChange',
  'onSubStateChange',
  'setState',
  'topState',
  'onStateBlur',
  'wrapper',
  'name',
  'keys',
  'depth',
  'outputState',
  'outputStates',
  'outputStats',
  'outputStatsList',
  'outputErrorCount',
  'evaluatedState',
  'componentExtensions',
  'validateImmediately',
  'handler',
  'debug',
  'converter',
  'schemaReducers',
  'hideErrorsUntilTouched'
]

export type SubFormProps<T extends StringIndexable = StringIndexable> = Omit<IFormProps, 'layout'> & {
  fields: TFormFieldDict<T>
  fieldArray?: Array<[string, FormFieldRef]>;
  reportChange: (fieldRef: FormFieldRef, nextValue?: TFormFactor) => void
  triggerBlur: (fieldRef: FormFieldRef) => void;
  evaluatedState?: any
  handler: UseFormReturn<T>
  layout?: HydratedFormLayoutProps<T>;
}

export const SUBFORM_PROPS = [...FORM_PROPS, 'fields', 'fieldArray', 'wrapper', 'reportChange', 'layout', 'triggerBlur']
